import { h } from 'preact'
import { useEffect, useRef, useState } from 'preact/hooks'

/**
 * BubbleReadReceipts — Messenger-style read/delivered watermark avatars.
 *
 * Shows avatars for members who have read or received this message.
 * The watermark algorithm (each person appears only on the latest message
 * they've reached) is handled upstream in chat-cable.js — this component
 * just renders whatever is in store.receiptsById for this message.
 *
 * Props: { message, store, personId }
 *
 * Reads store.receiptsById.value[message.id] → { readBy: [], deliveredTo: [] }
 *
 * Two-phase render: the row is first inserted with max-height:0 (base class
 * only), then on the next animation frame the --visible modifier is added so
 * the CSS max-height transition fires and the row grows in smoothly instead
 * of popping in at full height.
 */
export function BubbleReadReceipts({ message, store, personId }) {
  if (!message.id) return null

  const stored      = store.receiptsById.value[message.id]
  const rawReadBy   = stored?.readBy      ?? []
  const rawDelivered = stored?.deliveredTo ?? []

  // Filter out own person (shouldn't appear as "read by me")
  const readBy      = personId != null ? rawReadBy.filter(p => (p.id ?? p) !== personId) : rawReadBy
  const deliveredTo = personId != null ? rawDelivered.filter(p => (p.id ?? p) !== personId) : rawDelivered

  // Deduplicate: a person in readBy should not also appear in deliveredTo
  const readIds     = new Set(readBy.map(p => p.id ?? p))
  const deliveredFiltered = deliveredTo.filter(p => !readIds.has(p.id ?? p))

  // DM rooms have exactly one other participant — cap at 1 avatar as a
  // defense-in-depth safeguard against stale watermark bleed from other rooms.
  const room = store.activeRoom?.value
  const isDirect = room?.kind === 'direct' || room?.type === 'direct'
  const MAX_VISIBLE = isDirect ? 1 : 5

  const allVisible = [...readBy, ...deliveredFiltered]
  const capped     = allVisible.slice(0, MAX_VISIBLE)
  const cappedRead = capped.filter(p => readIds.has(p.id ?? p))
  const cappedDel  = capped.filter(p => !readIds.has(p.id ?? p))
  // Never show "+N" overflow in a DM — any excess there is bleed, not real members
  const excess     = isDirect ? 0 : allVisible.length - capped.length

  const hasContent = capped.length > 0

  // Two-phase visible state: false → insert collapsed row → rAF → true → CSS transition fires.
  const [visible, setVisible] = useState(false)
  const rafRef = useRef(null)

  useEffect(() => {
    if (hasContent && !visible) {
      // Double-rAF ensures the browser has painted the collapsed row before
      // adding --visible, so the transition actually runs.
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = requestAnimationFrame(() => setVisible(true))
      })
    }
    if (!hasContent && visible) {
      setVisible(false)
    }
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current) }
  }, [hasContent])

  if (!hasContent && !visible) return null

  return (
    <div class={`chat-read-receipt-row${visible ? ' chat-read-receipt-row--visible' : ''}`}>
      <div class="chat-read-receipts">
        {cappedRead.map((person, i) => (
          <ReceiptAvatar key={`r-${person.id ?? i}`} person={person} status="read" />
        ))}
        {cappedDel.map((person, i) => (
          <ReceiptAvatar key={`d-${person.id ?? i}`} person={person} status="delivered" />
        ))}
        {excess > 0 && (
          <span class="chat-read-receipt-more">+{excess}</span>
        )}
      </div>
    </div>
  )
}

function ReceiptAvatar({ person, status }) {
  const name      = person.name || ''
  const avatarUrl = person.avatar_url || null
  const initials  = name.slice(0, 1).toUpperCase() || '?'

  return (
    <span
      class={`chat-read-receipt-avatar chat-read-receipt-avatar--${status}`}
      title={name}
    >
      {avatarUrl
        ? <img src={avatarUrl} alt={name} width={16} height={16}
               onLoad={e => e.target.classList.add('is-loaded')}
               onError={e => { e.target.style.display = 'none' }}
            />
        : <span class="chat-read-receipt-initials">{initials}</span>
      }
    </span>
  )
}
